import React, { useMemo, useState } from 'react';
import {
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { layout, spacing, typography } from '../utils/theme';
import { useAppTheme } from '../utils/useAppTheme';
import { getBirthdayMetricExplanation } from '../data/birthdayMetricExplanations';

export const MetricHelpIcon = ({ color, size = 16 }) => (
  <Ionicons
    name="help-circle-outline"
    size={size}
    color={color}
    accessibilityElementsHidden
    importantForAccessibility="no"
  />
);

export const ExplainableMetric = ({
  metricKey,
  children,
  style,
  iconStyle,
  helpColor,
  showIcon = true,
  activeOpacity = 0.72,
}) => {
  const { colors } = useAppTheme();
  const [isOpen, setIsOpen] = useState(false);
  const info = getBirthdayMetricExplanation(metricKey);

  if (!info) return <View style={style}>{children}</View>;

  return (
    <>
      <TouchableOpacity
        style={style}
        onPress={event => {
          event?.stopPropagation?.();
          setIsOpen(true);
        }}
        activeOpacity={activeOpacity}
        accessibilityRole="button"
        accessibilityLabel={`${info.title}. Tap for an explanation.`}
        accessibilityHint="Opens details about what this result means and how it is calculated"
      >
        {children}
        {showIcon ? (
          <View style={iconStyle} pointerEvents="none">
            <MetricHelpIcon color={helpColor || colors.primary} />
          </View>
        ) : null}
      </TouchableOpacity>
      {isOpen ? (
        <MetricExplanationModal
          metricKey={metricKey}
          onClose={() => setIsOpen(false)}
        />
      ) : null}
    </>
  );
};

const MetricExplanationModal = ({ metricKey, onClose }) => {
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const info = getBirthdayMetricExplanation(metricKey);

  return (
    <Modal
      visible={Boolean(info)}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <TouchableOpacity
        style={styles.backdrop}
        activeOpacity={1}
        onPress={onClose}
        accessibilityRole="button"
        accessibilityLabel="Close explanation"
      >
        <View
          style={styles.dialog}
          accessibilityRole="alert"
          accessibilityViewIsModal
          accessibilityLabel={info ? `${info.title} explanation` : 'Metric explanation'}
        >

          <TouchableOpacity
            activeOpacity={1}
            onPress={event => event?.stopPropagation?.()}
          >
            <View style={styles.header}>
              <View style={styles.headerCopy}>
                <Ionicons name="help-circle" size={22} color={colors.primary} />
                <Text style={styles.title}>{info?.title}</Text>
              </View>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={onClose}
                accessibilityRole="button"
                accessibilityLabel="Close explanation"
                hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
              >
                <Ionicons name="close" size={22} color={colors.lightText} />
              </TouchableOpacity>
            </View>
            <ScrollView
              style={styles.body}
              contentContainerStyle={styles.bodyContent}
              bounces={false}
            >
              {info?.meaning ? (
                <View style={styles.section}>
                  <Text style={styles.sectionLabel}>What it means</Text>
                  <Text style={styles.bodyText}>{info.meaning}</Text>
                </View>
              ) : null}
              {info?.calculation ? (
                <View style={styles.section}>
                  <Text style={styles.sectionLabel}>How this app gets it</Text>
                  <Text style={styles.bodyText}>{info.calculation}</Text>
                </View>
              ) : null}
              {info?.note ? (
                <View style={styles.note}>
                  <Ionicons name="information-circle-outline" size={18} color={colors.primary} />
                  <Text style={styles.noteText}>{info.note}</Text>
                </View>
              ) : null}
            </ScrollView>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </Modal>
  );
};

const createStyles = colors => StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: colors.overlay,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
  },
  dialog: {
    width: '100%',
    maxWidth: 480,
    maxHeight: '75%',
    backgroundColor: colors.surface,
    borderRadius: layout.borderRadius.large,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
  },

  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  headerCopy: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  title: {
    flex: 1,
    color: colors.text,
    fontSize: typography.fontSize.medium,
    fontWeight: '700',
  },
  closeButton: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: -spacing.sm,
  },
  body: {
    flexGrow: 0,
  },
  bodyContent: {
    padding: spacing.md,
  },
  section: {
    marginBottom: spacing.md,
  },
  sectionLabel: {
    color: colors.primary,
    fontSize: typography.fontSize.small,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.xs,
  },
  bodyText: {
    color: colors.textSecondary,
    fontSize: typography.fontSize.regular,
    lineHeight: 21,
  },
  note: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    backgroundColor: colors.primarySurface,
    borderRadius: layout.borderRadius.medium,
    padding: spacing.sm,
  },
  noteText: {
    flex: 1,
    color: colors.textSecondary,
    fontSize: typography.fontSize.small,
    lineHeight: 18,
  },
});

export default MetricExplanationModal;
